/**
 * Chart settings: the user's line colours, chart type, scale and templates. Saved on the server when
 * signed in and cached on this device, so the chart paints with the right colours on the first frame
 * (and signed-out visitors keep theirs). While the settings popover is open, `preview` holds the
 * unsaved look so the chart shows each change as it is made.
 */
import { useEffect } from 'react';
import { chartLookFor, DEFAULT_CHART_PREFS, parseChartPrefs, type ChartColor, type ChartLook, type ChartPrefs, type Timeframe } from '@platform/shared';
import { api } from '../lib/api';
import { cssVar } from '../lib/theme';
import { createStore, useStore } from '../lib/store';
import { sessionStore, useMe } from './session';

const KEY = 'chartPrefs';

interface ChartState {
  prefs: ChartPrefs;
  preview: { symbol: string; look: ChartLook } | null;
  /** Settings popover open. Its gear sits in the account bar under the chart, not in the chart itself. */
  settingsOpen: boolean;
}

function readCached(): ChartPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? parseChartPrefs(JSON.parse(raw)) : DEFAULT_CHART_PREFS;
  } catch {
    return DEFAULT_CHART_PREFS;
  }
}

function cache(prefs: ChartPrefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* storage unavailable: settings last for this visit */
  }
}

export const chartStore = createStore<ChartState>({ prefs: readCached(), preview: null, settingsOpen: false });

/** Load the signed-in user's settings. Mount once. */
export function useChartSync() {
  const id = useMe()?.id ?? null;
  useEffect(() => {
    if (!id) return;
    api
      .get<ChartPrefs>('/api/chart-prefs')
      .then((prefs) => {
        chartStore.set({ prefs });
        cache(prefs);
      })
      .catch(() => {
        /* keep the cached settings */
      });
  }, [id]);
}

/** Save on this device, and on the server when signed in. */
export async function saveChartPrefs(prefs: ChartPrefs): Promise<ChartPrefs> {
  const saved = sessionStore.get().me ? await api.put<ChartPrefs>('/api/chart-prefs', prefs) : parseChartPrefs(prefs);
  chartStore.set({ prefs: saved });
  cache(saved);
  return saved;
}

export const setChartPreview = (preview: ChartState['preview']) => chartStore.set({ preview });

export const setChartSettingsOpen = (settingsOpen: boolean) => chartStore.set({ settingsOpen });
export const useChartSettingsOpen = () => useStore(chartStore, (s) => s.settingsOpen);

export const useChartPrefs = () => useStore(chartStore, (s) => s.prefs);

/** The look the chart for `symbol` draws: the unsaved preview while editing, else the saved one. */
export const useChartLook = (symbol: string) =>
  useStore(chartStore, (s) => (s.preview?.symbol === symbol ? s.preview.look : chartLookFor(s.prefs, symbol)));

const THEME_VARS: Record<string, string> = { up: '--green', down: '--red', warn: '--orange', accent: '--accent', text: '--text', muted: '--muted' };

/** CSS colour for a setting; `auto` resolves to `fallback` (the caller decides what auto means). */
export function chartColor(c: ChartColor, fallback = ''): string {
  if (c === 'auto') return fallback;
  if (c.startsWith('theme:')) return cssVar(THEME_VARS[c.slice(6)] ?? '--text');
  return c;
}

/**
 * The candle the trade chart is drawing: its time frame and open. The price headers show the move from
 * that open to the live price, so the change follows the time frame picked on the chart.
 */
export interface ChartCandle {
  symbol: string;
  tf: Timeframe;
  /** start of the candle (ms) */
  t: number;
  o: number;
}

const candleStore = createStore<{ live: ChartCandle | null }>({ live: null });

export function setChartCandle(live: ChartCandle | null) {
  const cur = candleStore.get().live;
  if (cur === live || (cur && live && cur.symbol === live.symbol && cur.tf === live.tf && cur.t === live.t && cur.o === live.o)) return;
  candleStore.set({ live });
}

/** The chart's current candle for `symbol`, or null while no chart shows it. */
export const useChartCandle = (symbol: string) => useStore(candleStore, (s) => (s.live?.symbol === symbol ? s.live : null));
